import {Component, OnInit, computed, inject, signal} from '@angular/core';
import {DatePipe} from '@angular/common';
import {MatTooltipModule} from '@angular/material/tooltip';
import {CheckboxComponent} from '../../../_ui/components/controls/checkbox/checkbox.component';
import {SelectComponent} from '../../../_ui/components/controls/select/select.component';
import {GrantHttpService} from './grant-http.service';
import {GrantChangeNotifierService} from './grant-change-notifier.service';
import {GrantScopeService, GrantTargetCompany, GrantTargetShard} from './grant-scope.service';
import {AuthenticationService} from '../../../_auth/authentication.service';
import {ExternalIdentityInfo} from '../../../models/admin_owner/user/external-identity-info';
import {InviteCodeDialogComponent} from './invite-code-dialog.component';
import {InviteDeliveryChoiceComponent} from './invite-delivery-choice.component';
import {ResendConfirmDialogComponent} from './resend-confirm-dialog.component';
import {RevokeConfirmDialogComponent, RevokeTarget} from './revoke-confirm-dialog.component';
import {ViewportService} from '../../../_services/viewport.service';
import {
  DirectGrantResult,
  GrantScope,
  InvitationStatus,
  InviteDelivery,
  RoleGrant,
  RoleGrantInvitation,
  RoleGrantInviteRequest,
  bindingSentence,
  coverageSentence,
  deliverySentence,
  directGrantErrorMessage,
  directGrantTargetLabel,
  grantErrorMessage,
  grantableRoles,
  invitationCanBeResent,
  invitationStatus,
  invitationStatusClass,
  invitationStatusLabel,
  looksLikeEmail,
  resendErrorMessage,
  roleDescription,
  roleLabel,
  roleListLabel,
  scopeLabel,
  supersededGrants
} from './grant.models';

/**
 * One entry of the *Geltungsbereich* picker.
 *
 * `cqme-select` is generic over `TItem extends object`, so the two scopes have to be objects rather than the
 * bare enum members a native `<option>` could carry. `value` holds the {@link GrantScope} **ordinal**, which is
 * what the backend binds — see the note on `GrantScope` in `grant.models.ts`.
 */
interface ScopeOption {
  value: GrantScope;
  label: string;
}

/**
 * The two ways access is created from this screen (#749).
 *
 * `invite` mints a one-time code somebody redeems; `direct` hands an existing account its roles outright.
 * They are two tabs over ONE form rather than two forms, because most of what they ask — scope, location,
 * roles — is the same question.
 */
type AccessTab = 'invite' | 'direct';

/** One row of the assignment tab's Person picker: the account, described the way somebody recognises it. */
interface IdentityOption {
  id: string;
  /** „Name (adresse@example.com) · Microsoft" — one string, so the search box matches on all three parts. */
  label: string;
}

/**
 * Managing **who may act inside a company**: issuing invitations, watching them through their life, revoking
 * them, and revoking the grants they produced.
 *
 * ### Where this lives, and why
 *
 * Route `company/access`, inside the company module — the same place as every other "administer this company"
 * screen, and beside `company/payroll`, which is a specialised view of this exact mechanism (a payroll
 * invitation *is* a company-scoped `RoleGrantInvitation` since the #261 fold). The company module is also
 * where the axis of the API lives: every `/grants` endpoint is keyed by `companyId`.
 *
 * It is reachable from two navigation entries pointing at the one route, because two genuinely different
 * sessions need it:
 *
 * - the **Unternehmen** sub-navbar, for a session that is already inside a company (company owner, shard
 *   admin);
 * - the **Owner** sub-navbar, for a platform principal — whose session is company-*independent*, so
 *   `NavbarService` hides every company-level entry from it (`PlatformLevelTitles`) and it would otherwise
 *   have no way in.
 *
 * ### The platform owner has no company, so the company is a choice
 *
 * `userValue.company.id` is **absent** for a platform principal — assuming it exists is how this screen would
 * have loaded `/grants/undefined`. The company is therefore explicit state: fixed to the session's own company
 * when there is one, and a picker when there is not. Nothing loads until it is set, and the screen says so
 * rather than showing empty tables that read as "no invitations".
 *
 * ### The one-time code
 *
 * Issuing hands back a code that exists in that response and nowhere else, ever. It is surfaced by
 * {@link InviteCodeDialogComponent}, which cannot be dismissed by accident. The list afterwards shows the
 * invitation as **Offen** with an explicit "code no longer available" note in place of the code, because the
 * absence is a property of the product, not a loading state — and the row offers **Neu ausstellen** for the
 * only remedy there is.
 *
 * ### „Erneut senden" and „Neu ausstellen" are two actions, not one with two names (#579)
 *
 * They look like the same thing and are not, and the split is forced by the server rather than chosen here.
 * Verified against `RoleGrantService.ResendInvitation` and {@link reissue} below:
 *
 *  * **„Erneut senden"** (`POST /grants/invitations/{id}/resend`) withdraws the invitation, issues a
 *    replacement carrying the same scope, roles, label, binding and enforcement, **and mails it** to the bound
 *    address. Without a usable bound address the server refuses it outright
 *    (`E_GRANT_INVITE_EMAIL_REQUIRED`) — there is nowhere to send it — so the button is only offered for an
 *    invitation that actually has one.
 *  * **„Neu ausstellen"** ({@link reissue}) issues an identical invitation and revokes the old one, and
 *    deliberately does **not** mail anything: it is the action taken when the code was lost, so the person
 *    doing it is standing in front of the dialog waiting for a link to hand over. It therefore stays available
 *    for every pending invitation, bound or not.
 *
 * So neither replaces the other. The one thing they share is the consequence the dialog has to name: the
 * previous code stops working immediately, and nobody holding it is told.
 *
 * Expiry does **not** disqualify a re-send — see {@link invitationCanBeResent}. An expired invitation is the
 * ordinary reason to press the button, and the server lets exactly that case through on purpose.
 *
 * ### Revoking asks first, outside the row (#279 follow-up)
 *
 * Both revoke actions — an invitation's and a single grant's — are confirmed in a modal
 * ({@link RevokeConfirmDialogComponent}), never inline in the table cell that started them. The inline version
 * grew the row (a one-line link became a sentence plus two buttons in the same `<td>`), so every row below it
 * shifted under the cursor — the exact shape of mis-click that once revoked the wrong invitation during live
 * testing. One dialog instance serves all four sites (both tables, both mobile card lists), driven off the
 * single {@link pendingRevoke} signal, so a row never carries its own confirmation state.
 *
 * ### A covered grant says so, and can be revoked together with the one covering it (#759)
 *
 * A role that cascades — `CompanyOwner`, a company-scoped `ShiftLead` — confers everything a narrower
 * shard-scoped grant does, so the narrower rows keep sitting in this roster conferring nothing. Two things
 * follow from that, and the first is what makes the second unsurprising:
 *
 *  * every covered row carries a neutral **`[abgedeckt]`** badge and names what covers it
 *    ({@link coverageSentence}) — informational, not a warning: nothing is wrong, the grant is simply
 *    inert while the wider one stands;
 *  * revoking the wider one opens the dialog with the survivor list and a second action
 *    ({@link confirmRevokeAll}), because "I removed their CompanyOwner" was never "I removed their access".
 *
 * Redundancy itself is **not decided here**. The backend derives it from `RoleGrantExpander.Expand`, so the
 * rule cannot drift from the privilege computation; this screen reads the answer through the one function
 * {@link grantCoverage} and renders it.
 *
 * ### Every role says what it confers — one description, two mechanisms, never both at once
 *
 * A role checkbox is a security decision, so each one carries {@link roleDescription} — derived from the
 * backend gates, not from the role's name. A tooltip is hover-driven and a phone has no hover, so the sentence
 * has to arrive some other way below `md`. {@link compact} picks the mechanism, and it is the ONLY thing that
 * decides, so the two can never both be live:
 *
 *  * **below `md`** the sentence is ordinary visible text under the label, and `matTooltipDisabled` stops
 *    Material describing the wrapper at all — the one copy a screen reader gets is the one on the screen;
 *  * **from `md` up** that text is not in the DOM, and the tooltip's `aria-describedby` is the only
 *    description — reachable through the pointer and the focus monitor a desktop actually has.
 *
 * It used to be both mechanisms at every width, held to one announced copy by marking the visible text
 * `aria-hidden="true"`. That kept the count right only while a Tailwind `md:hidden` class and an ARIA attribute
 * agreed by hand, and it bought the agreement by taking the copy a phone user can SEE out of the accessibility
 * tree. `matTooltipDisabled` rather than stripping the attribute afterwards: `aria-describedby` here belongs to
 * the CDK's `AriaDescriber`, which re-attaches it on the next message or `disabled` change and would silently
 * undo a hand-written removal.
 *
 * The tooltip sits on a **wrapper**, never as a new input on {@link CheckboxComponent}: that control is shared
 * with the configuration and role screens, and a design-system component should not grow an API because one
 * caller needs a hint.
 */
@Component({
  selector: 'company-access-management',
  standalone: true,
  imports: [
    DatePipe, InviteCodeDialogComponent, InviteDeliveryChoiceComponent, ResendConfirmDialogComponent,
    RevokeConfirmDialogComponent, CheckboxComponent, SelectComponent, MatTooltipModule
  ],
  template: `
    <div class="mx-auto max-w-5xl space-y-6 p-4">

      <div>
        <h1 class="text-2xl font-bold tracking-tight text-gray-900">Zugänge</h1>
        <p class="mt-1 text-sm text-gray-500">
          Laden Sie Personen mit einem eigenen Konto in diese Firma ein und verwalten Sie, wer welche Rolle
          hat. Eingeladene melden sich mit ihrem eigenen Konto an — es wird kein Benutzer in Ihrer Firma
          angelegt.
        </p>
      </div>

      @if (!canManage()) {
        <div class="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
          <p class="text-sm font-bold text-gray-800">Keine Berechtigung</p>
          <p class="mt-1 text-sm text-gray-500">
            Zugänge verwalten dürfen Plattformverwaltung, Firmeninhaber:innen und Standortverwaltungen.
          </p>
        </div>
      } @else {

        <!-- Company axis. A session that already has a company shows it; a platform principal picks one. -->
        <div class="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
          @if (needsCompanyChoice()) {
            <label for="grant-company" class="mb-2 block text-sm font-bold tracking-tight text-gray-700">
              Firma
            </label>
            <!-- The design-system control, not a raw select: a platform principal's tenant list is unbounded,
                 and type-to-filter is the only thing that keeps it usable past a handful of companies.
                 (No backticks anywhere in this template: it is a template literal — style guide gotcha.)

                 The "?? undefined" is load-bearing. ng-select treats null/'' as a DEFINED value, fails to
                 match it against any item, and then synthesises a blank selected entry — which renders as an
                 empty box where the placeholder belongs. Only undefined itself means "nothing chosen". -->
            <cqme-select id="grant-company" size="md" [options]="companies()" [labelKey]="'name'"
                         [valueKey]="'id'" [clearable]="true" placeholder="Firma wählen …"
                         ariaLabel="Firma, deren Zugänge verwaltet werden"
                         [current]="companyId() ?? undefined"
                         (currentChange)="chooseCompany(one($event))"/>
            <p class="mt-2 text-sm text-gray-500">
              Ihr Konto ist keiner Firma zugeordnet. Wählen Sie die Firma, deren Zugänge Sie verwalten möchten.
            </p>
            @if (!companies().length && !loadingCompanies()) {
              <p class="mt-2 text-sm font-semibold text-red-700" role="alert">
                Die Firmenliste konnte nicht geladen werden.
                <button type="button" (click)="loadCompanies()" class="underline">Erneut versuchen</button>
              </p>
            }
          } @else {
            <p class="text-xs font-semibold uppercase tracking-wide text-gray-500">Firma</p>
            <p class="mt-0.5 text-base font-bold tracking-tight text-gray-800">{{ companyName() }}</p>
          }
        </div>

        @if (!companyId()) {
          <div class="rounded-2xl border-2 border-dashed border-gray-100 p-6 text-center text-sm italic
                      text-gray-500">
            Wählen Sie oben eine Firma, um deren Einladungen und Zugänge zu sehen.
          </div>
        } @else {

          <!-- ── Create access: ONE card, two tabs (#749) ──────────────────────────
               There used to be an invitation card and, below it, a second card that opened a full-screen
               dialog for direct assignment. Two surfaces asking most of the same questions — scope, location,
               roles — in two places, and the dialog was too large to read comfortably. The two paths are now
               two tabs over ONE form: the shared questions are asked once and keep their answers across a
               switch, and the three fields that genuinely differ swap in place.
               (No backticks anywhere in this template: it is a template literal — style guide gotcha.) -->
          <div>
            <!-- No tab strip when there is only one tab. A lone tab is a control that cannot do anything,
                 and it would tell a company owner that something exists which they may never reach — so a
                 session without the direct path sees exactly the invitation form it saw before. -->
            @if (showTabs()) {
              <div role="tablist" aria-label="Art des Zugangs" (keydown)="onTabKeydown($event)"
                   class="flex gap-1 border-b-[1px] border-gray-200">
                <!-- The active tab is marked by WEIGHT and a 2px underline as well as colour, so the
                     selection survives a monochrome display or a colour-vision difference. tabindex is
                     roving (APG): one stop for the whole strip, arrow keys move within it. -->
                <button type="button" role="tab" id="tab-invite" data-test="tab-invite"
                        aria-controls="access-create-panel" [attr.aria-selected]="onInvite()"
                        [attr.tabindex]="onInvite() ? 0 : -1" (click)="selectTab('invite')"
                        class="-mb-px border-b-2 px-4 py-2.5 text-sm transition-all duration-200
                               focus-visible:outline-none focus-visible:ring-2
                               focus-visible:ring-theme-primary-500"
                        [class.border-theme-primary-600]="onInvite()" [class.font-bold]="onInvite()"
                        [class.text-gray-900]="onInvite()"
                        [class.border-transparent]="!onInvite()" [class.font-semibold]="!onInvite()"
                        [class.text-gray-600]="!onInvite()">
                  Einladen
                </button>
                <button type="button" role="tab" id="tab-direct" data-test="tab-direct"
                        aria-controls="access-create-panel" [attr.aria-selected]="onDirect()"
                        [attr.tabindex]="onDirect() ? 0 : -1" (click)="selectTab('direct')"
                        class="-mb-px border-b-2 px-4 py-2.5 text-sm transition-all duration-200
                               focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                        [class.border-amber-500]="onDirect()" [class.font-bold]="onDirect()"
                        [class.text-gray-900]="onDirect()"
                        [class.border-transparent]="!onDirect()" [class.font-semibold]="!onDirect()"
                        [class.text-gray-600]="!onDirect()">
                  Ohne Einladung zuweisen
                </button>
              </div>
            }

            <!-- One panel, two surfaces. The class list is COMPUTED rather than a stack of [class.x]
                 bindings because the invite surface keeps its existing /50 opacity utilities, and a slash
                 is not something a [class.…] binding key can carry. No tabindex on the panel: it is full
                 of focusable controls, so APG does not ask for one. -->
            <div id="access-create-panel" [class]="panelClass()"
                 [attr.role]="showTabs() ? 'tabpanel' : null"
                 [attr.aria-labelledby]="showTabs() ? activeTabId() : null">

              @if (onInvite()) {
                @if (!showTabs()) {
                  <h2 class="text-base font-bold tracking-tight text-gray-800">Einladen</h2>
                }
              } @else {
                <!-- ── The risk panel. Always visible, at the top of the tab — never a step that appears
                     once somebody has already decided to press the button. It is the reason this path is
                     slower than the one beside it. -->
                <section class="rounded-2xl border-[1px] border-amber-300 bg-white p-4"
                         aria-labelledby="direct-grant-risk-heading" data-test="direct-grant-risk">
                  <!-- h2, not h3, and it is not a styling choice: with a tab strip the card carries no
                       heading of its own (the tablist labels the panel), so the page runs h1 → this. An h3
                       here skips a level and axe calls it a heading-order violation. -->
                  <h2 id="direct-grant-risk-heading" class="text-sm font-bold tracking-tight text-gray-900">
                    Was diese Zuweisung bedeutet
                  </h2>
                  <ul class="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-700">
                    <li>Die Rechte gelten sofort. Die Person muss nichts bestätigen.</li>
                    <li>
                      Wir senden keine Einladung und keine E-Mail. Niemand prüft, ob die Adresse zu dieser
                      Person gehört.
                    </li>
                    <li>
                      Das Prüfprotokoll hält den Vorgang unter Ihrem Namen fest. Der Eintrag bleibt, auch wenn
                      Sie die Rechte später entziehen.
                    </li>
                  </ul>
                </section>

                @if (directResult(); as result) {
                  <div class="mt-3 rounded-xl border-[1px] border-green-200 bg-green-50 p-3" role="status">
                    <p class="text-sm font-bold text-green-800">Rechte zugewiesen</p>
                    <p class="mt-1 text-sm text-gray-700" data-test="direct-grant-confirmation">
                      {{ directConfirmation(result) }}
                    </p>
                    @if (result.reactivated) {
                      <!-- Not a footnote: the account had been switched OFF because it held nothing, and the
                           assignment switched it back on. The operator restored a login somebody had removed,
                           which is more than they asked for and more than the sentence above says. -->
                      <p class="mt-2 rounded-xl border-[1px] border-amber-300 bg-amber-50 px-3 py-2 text-sm
                                font-semibold text-gray-800" data-test="direct-grant-reactivated">
                        Dieses Konto war deaktiviert. Die Zuweisung hat es wieder aktiviert.
                      </p>
                    }
                    <p class="mt-1 text-sm text-gray-700">
                      Der Vorgang steht im Prüfprotokoll unter „RoleGrant / DirectlyGranted“.
                    </p>
                  </div>
                }
              }

            <div class="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
              @if (scopes().length > 1) {
                <div>
                  <label for="grant-scope" class="mb-2 block text-sm font-bold tracking-tight text-gray-700">
                    Geltungsbereich
                  </label>
                  <!-- Not clearable: "no scope" is not a state the request can carry, so the control must
                       never offer it. The two entries come from the scopeOptions field rather than being written out
                       here, because [valueKey] needs a real field to bind the GrantScope ORDINAL to — and
                       that ordinal is what the backend reads. -->
                  <!-- The accessible name follows the TAB: the control is shared, but "der Einladung" is
                       false on the assignment tab and a screen-reader user has no other way to tell. -->
                  <cqme-select id="grant-scope" size="md" [options]="scopeOptions" [labelKey]="'label'"
                               [valueKey]="'value'" [ariaLabel]="scopeAriaLabel()"
                               [current]="scope()" (currentChange)="setScope($event)"/>
                </div>
              } @else {
                <div>
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-500">Geltungsbereich</p>
                  <p class="mt-0.5 text-sm font-semibold text-gray-800">Einzelner Standort</p>
                  <!-- GrantIssuancePolicy refuses a company-scope invitation from a shard admin: it would
                       widen their own reach past the shard they administer. Saying so beats a 403. -->
                  <p class="mt-1 text-sm text-gray-500">
                    Als Standortverwaltung können Sie nur für Ihren eigenen Standort einladen.
                  </p>
                </div>
              }

              @if (scope() === Scope.Shard) {
                <div>
                  <label for="grant-shard" class="mb-2 block text-sm font-bold tracking-tight text-gray-700">
                    Standort
                  </label>
                  <!-- The "|| undefined" is there for the same reason as on the company picker: the signal holds '' for
                       "not chosen", and ng-select would render that empty string as a blank selected item. -->
                  <cqme-select id="grant-shard" size="md" [options]="shards()" [labelKey]="'name'"
                               [valueKey]="'id'" [clearable]="true" placeholder="Standort wählen …"
                               [ariaLabel]="shardAriaLabel()"
                               [current]="shardId() || undefined"
                               (currentChange)="shardId.set(one($event))"/>
                  @if (!shards().length) {
                    <p class="mt-2 text-sm text-gray-500">
                      Für diese Firma sind hier keine Standorte verfügbar.
                    </p>
                  }
                </div>
              }
            </div>

            <fieldset class="mt-4">
              <legend class="mb-2 block text-sm font-bold tracking-tight text-gray-700">Rollen</legend>
              <!-- One column on a phone (each role carries a sentence under it, which cannot sit in a
                   horizontal strip), the compact wrapping strip from md up. -->
              <div id="role-options"
                   class="flex flex-col gap-y-3 md:flex-row md:flex-wrap md:gap-x-6 md:gap-y-3">
                @for (role of offeredRoles(); track role) {
                  <!-- The tooltip lives HERE rather than on cqme-checkbox: Material's mouseenter/focus
                       monitoring covers descendants, so hovering the box or tabbing into it opens the hint —
                       without giving a shared design-system control an input two other screens do not want. -->
                  <div class="md:max-w-xs" [class.cursor-help]="!compact() && !!description(role)"
                       [matTooltip]="description(role)" [matTooltipDisabled]="compact()"
                       matTooltipClass="prose-tooltip" matTooltipPosition="above">
                    <cqme-checkbox [id]="'role-' + role" [label]="label(role)"
                                   [checked]="roles().includes(role)" (checkedChange)="toggleRole(role)"/>
                    @if (compact() && description(role)) {
                      <p class="ml-7 mt-0.5 text-xs text-gray-500">{{ description(role) }}</p>
                    }
                  </div>
                }
              </div>
            </fieldset>

            <!-- ── The two swapped slots ─────────────────────────────────────────────
                 Same two positions on both tabs, different question in each: the note an invitation carries
                 becomes the reason an assignment REQUIRES, and the address an invitation is sent to becomes
                 the account an assignment is handed to. Swapping in place rather than stacking two forms is
                 the whole point of the tabs — the operator's eye does not have to move. -->
            <div class="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                @if (onInvite()) {
                  <label for="grant-label" class="mb-2 block text-sm font-bold tracking-tight text-gray-700">
                    Notiz (optional)
                  </label>
                  <input id="grant-label" type="text" [value]="labelText()"
                         (input)="labelText.set(value($event))" placeholder="z. B. Steuerbüro Meier"
                         class="block w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm
                                shadow-sm transition-all duration-200 placeholder-gray-300
                                focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500">
                } @else {
                  <label for="grant-reason" class="mb-2 block text-sm font-bold tracking-tight text-gray-700">
                    Grund (erforderlich)
                  </label>
                  <!-- ONE listener on the value, never [(ngModel)] plus a second (ngModelChange): a second
                       listener on the same output runs in template order and reads the PREVIOUS value (#273). -->
                  <input id="grant-reason" type="text" autocomplete="off" [value]="reason()"
                         (input)="reason.set(value($event))" aria-describedby="grant-reason-hint"
                         placeholder="z. B. Ersteinrichtung, Zugang wiederhergestellt"
                         class="block w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm
                                shadow-sm transition-all duration-200 placeholder-gray-300
                                focus:border-amber-500 focus:ring-1 focus:ring-amber-500">
                  <p id="grant-reason-hint" class="mt-1 text-sm text-gray-700">
                    Der Grund steht im Prüfprotokoll.
                  </p>
                }
              </div>
              <div>
                @if (onDirect()) {
                  <label for="grant-identity" class="mb-2 block text-sm font-bold tracking-tight text-gray-700">
                    Person
                  </label>
                  <!-- The design-system control, never a native select: an installation-wide account
                       directory is unbounded, and a native select's type-ahead would leak the list to
                       anybody watching the screen. -->
                  <cqme-select id="grant-identity" size="md" [options]="identities()" [labelKey]="'label'"
                               [valueKey]="'id'" [clearable]="true" placeholder="Konto wählen …"
                               ariaLabel="Konto, das die Rechte erhält"
                               [current]="identityId() || undefined"
                               (currentChange)="identityId.set(one($event))"/>
                  @if (identitiesFailed()) {
                    <p class="mt-2 text-sm font-semibold text-red-700" role="alert">
                      Die Kontenliste konnte nicht geladen werden.
                      <button type="button" (click)="loadIdentities()" class="underline">
                        Erneut versuchen
                      </button>
                    </p>
                  } @else if (!identities().length && !loadingIdentities()) {
                    <p class="mt-2 text-sm text-gray-700">
                      Es sind noch keine Konten vorhanden. Laden Sie die Person stattdessen ein.
                    </p>
                  }
                }
                @if (onInvite()) {
                <!-- Named for what the address IS rather than for one of the things it can do (#584). It used to
                     read "Bindung an E-Mail", which stopped being true the moment delivery and binding became
                     two decisions: the ordinary invitation is mailed to this address and redeemable by whoever
                     holds the link. -->
                <label for="grant-email" class="mb-2 block text-sm font-bold tracking-tight text-gray-700">
                  E-Mail-Adresse (optional)
                </label>
                <input id="grant-email" type="email" [value]="boundEmail()"
                       (input)="boundEmail.set(value($event))" placeholder="name@firma.de"
                       class="block w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm
                              shadow-sm transition-all duration-200 placeholder-gray-300
                              focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500">
                <p class="mt-1 text-sm text-gray-500">
                  Ohne Adresse geben Sie den Einladungslink selbst weiter.
                </p>
                }
              </div>
            </div>

            <!-- ── The tab-only section ──────────────────────────────────────────────
                 Everything below here belongs to exactly one of the two paths and has no counterpart on the
                 other, so it is not a swapped slot: an invitation decides how it travels and who may redeem
                 it; an assignment decides how long it lasts. -->
            @if (onDirect()) {
              <div class="mt-4 md:w-1/2 md:pr-1.5">
                <label for="grant-duration" class="mb-2 block text-sm font-bold tracking-tight text-gray-700">
                  Dauer
                </label>
                <cqme-select id="grant-duration" size="md" [options]="durations" [labelKey]="'label'"
                             [valueKey]="'hours'" ariaLabel="Dauer der Zuweisung"
                             [current]="durationHours() ?? PERMANENT"
                             (currentChange)="setDuration($event)"/>
                <p class="mt-1 text-sm text-gray-700">{{ durationHint() }}</p>
              </div>
            } @else {
              <div class="mt-4">
                <!-- ── Delivery, and who may redeem ──────────────────────────────────
                     Both stand on the address above, so both are unavailable without one — the server refuses
                     the combination (E_GRANT_INVITE_EMAIL_REQUIRED) and a control that can be switched on into
                     a guaranteed refusal is worse than one that is visibly not yet available. They stay
                     RENDERED rather than hidden, so the reason to type an address is visible before it is
                     typed.

                     Delivery is the SHARED segmented choice (#568) — the same control, the same two words, on
                     all three screens that issue an invitation. It used to be a switch here, and a select on
                     the roster, and nothing at all on an employee's Anmeldung card; one decision asked three
                     ways is three places to drift.

                     The binding below keeps its switch: it is a genuine on/off narrowing of an invitation that
                     exists either way, not a choice between two outcomes. The switch markup is the profile
                     page's "Passwort-Login" pattern — the label wraps only the control and its track, the words
                     sit in the sibling block above, so it needs the explicit aria-labelledby/-describedby
                     association or it announces as an unnamed checkbox. -->
                <div class="mt-3 space-y-2">
                  <!-- The two sentences are single-line on purpose: they are bound as plain string inputs and
                       rendered into a text node, so a wrapped literal would carry its own newline and
                       indentation into the copy (and into every assertion about it). -->
                  <company-invite-delivery-choice
                    name="invite-deliver" [value]="deliveryChoice()" [emailAvailable]="emailUsable()"
                    unavailableReason="Per E-Mail senden ist erst möglich, wenn eine E-Mail-Adresse eingetragen ist."
                    hint="Wir senden den Einladungslink an die Adresse oben. Er wird Ihnen trotzdem angezeigt."
                    (valueChange)="setDeliveryChoice($event)"/>

                  <!-- ONE binding toggle (#584), where there used to be two controls saying almost the same
                       thing. "Bindung an E-Mail" and "E-Mail-Adresse verbindlich" were separate, and their only
                       distinguishable state — bound but not enforced — refuses nobody: the gate falls back to
                       the code alone for any address the provider does not vouch for. So the pair offered a
                       choice between a rule and a rule that does nothing, and the difference was invisible on
                       the screen that made it. This switch decides the one question an issuer actually has,
                       and the request carries BoundEmail and EnforceBoundEmail together or neither. -->
                  <div class="flex items-start justify-between gap-3 rounded-xl border border-gray-200 p-3"
                       [class.bg-white]="emailUsable()" [class.bg-gray-50]="!emailUsable()">
                    <span class="min-w-0">
                      <span id="invite-binding-label" class="block text-sm font-medium text-gray-900">
                        Nur mit dieser E-Mail-Adresse einlösbar
                      </span>
                      <span id="invite-binding-hint" class="block text-xs text-gray-700">
                        Eingeschaltet: nur ein Konto, dessen vom Anbieter bestätigte Adresse genau dieser
                        entspricht, kann die Einladung annehmen. Apple „E-Mail-Adresse verbergen“ wird
                        abgelehnt. Ausgeschaltet: jedes Konto kann sie annehmen.
                      </span>
                    </span>
                    <label class="relative inline-flex shrink-0 cursor-pointer items-center">
                      <input id="invite-binding" type="checkbox" class="peer sr-only"
                             [checked]="bindToEmail()" [disabled]="!emailUsable()"
                             aria-labelledby="invite-binding-label" aria-describedby="invite-binding-hint"
                             (change)="setBindToEmail(checked($event))">
                      <span class="h-6 w-11 rounded-full bg-gray-300 after:absolute after:left-0.5
                                   after:top-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white
                                   after:transition-all peer-checked:bg-theme-primary-600
                                   peer-checked:after:translate-x-5 peer-disabled:cursor-not-allowed
                                   peer-disabled:opacity-50"></span>
                    </label>
                  </div>

                  <!-- Said once, under both switches, only while the binding is actually on: it is the one
                       consequence an issuer cannot discover afterwards, because the refusal happens on the
                       invitee's screen. gray-700 rather than a hint grey — at text-xs the lighter shade fails
                       WCAG AA, and axe calls it "serious". -->
                  @if (bindToEmail()) {
                    <p class="text-xs text-gray-700" role="status" aria-live="polite">
                      Achtung: Meldet sich die eingeladene Person mit Apple „E-Mail-Adresse verbergen“ oder mit
                      einem Konto ohne bestätigte Adresse an, wird die Einladung abgewiesen.
                    </p>
                  }
                </div>
              </div>
            }

            <!-- One refusal line per tab, never a shared one: the two calls fail for different reasons and a
                 message left over from the other form is worse than none. -->
            @if (currentError(); as message) {
              <p class="mt-3 rounded-2xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" role="alert"
                 data-test="create-access-error">
                {{ message }}
              </p>
            }

            <div class="mt-4 flex justify-end">
              <!-- One button, and its label says which of the two things it does. The assignment tab gets the
                   danger shape rather than the primary one: it is the only write in the product that creates
                   access for somebody who is never asked, and it should not look like the invitation beside
                   it. -->
              <button type="button" (click)="submitCurrent()" [disabled]="!canSubmitCurrent()"
                      data-test="create-access-submit" [class]="submitClass()">
                {{ submitLabel() }}
              </button>
            </div>
            </div>
          </div>

          <!-- ── Grants ───────────────────────────────────────────────────────────── -->
          <!-- Erteilte Rollen comes FIRST, and Einladungen below it. "Who currently has access" is the
               standing state of the company and the question this screen is opened to answer; an invitation
               is transient scaffolding that exists only until it is redeemed or expires. Pinned by a spec, so
               an edit cannot quietly put the transient thing back on top. -->
          <section class="space-y-3">
            <div>
              <h2 class="text-base font-bold tracking-tight text-gray-800">Erteilte Rollen</h2>
              <p class="mt-1 text-sm text-gray-500">
                Jede Rolle ist ein eigener Eintrag und lässt sich einzeln entziehen — die übrigen Rollen
                derselben Einladung bleiben bestehen.
              </p>
            </div>

            @if (loading()) {
              <p class="py-8 text-center text-sm text-gray-500" role="status" aria-live="polite">
                Zugänge werden geladen …
              </p>
            } @else if (!grants().length) {
              <p class="rounded-2xl border-2 border-dashed border-gray-100 py-6 text-center text-sm italic
                        text-gray-500">
                Noch keine erteilten Rollen.
              </p>
            } @else {
              <div id="grants-table"
                   class="hidden overflow-x-auto rounded-2xl border border-gray-100 bg-white shadow-sm
                          md:block">
                <table class="min-w-full divide-y divide-gray-100 text-sm">
                  <thead class="bg-gray-50">
                    <tr class="text-left text-xs uppercase tracking-wide text-gray-500">
                      <th scope="col" class="px-4 py-3 font-semibold">Person</th>
                      <th scope="col" class="px-4 py-3 font-semibold">Rolle</th>
                      <th scope="col" class="px-4 py-3 font-semibold">Geltungsbereich</th>
                      <th scope="col" class="px-4 py-3 font-semibold">Status</th>
                      <th scope="col" class="px-4 py-3 text-right font-semibold">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-100">
                    @for (row of grantRows(); track row.grant.id) {
                      @let grant = row.grant;
                      <tr class="transition-colors hover:bg-gray-50">
                        <td class="px-4 py-3 font-medium text-gray-900">
                          {{ grant.identityName || 'Unbekanntes Konto' }}
                        </td>
                        <td class="px-4 py-3 text-gray-700">
                          <!-- #759: a covered grant confers nothing new, so the badge is NEUTRAL — it reports
                               a state, it does not warn about one. The covering role is named beneath the
                               row rather than in a tooltip, so a phone reads the same sentence a desktop
                               does and there is only ever one copy of it in the accessibility tree. -->
                          <div class="flex flex-wrap items-center gap-2">
                            <span>{{ label(grant.role) }}</span>
                            @if (row.coverage) {
                              <span class="inline-flex shrink-0 items-center rounded-full bg-gray-100 px-2
                                           py-0.5 align-top text-xs font-semibold text-gray-600">
                                abgedeckt
                              </span>
                            }
                          </div>
                          @if (row.coverage; as coverage) {
                            <p class="mt-1 text-xs text-gray-600">{{ coverage }}</p>
                          }
                        </td>
                        <td class="px-4 py-3 text-gray-500">
                          {{ grant.shardId ? 'Standort' : 'Gesamte Firma' }}
                        </td>
                        <td class="px-4 py-3">
                          <span class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold"
                                [class]="grant.revokedAt ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'">
                            {{ grant.revokedAt ? 'Widerrufen' : 'Aktiv' }}
                          </span>
                        </td>
                        <td class="px-4 py-3 text-right">
                          @if (!grant.revokedAt) {
                            <button type="button" (click)="askRevokeGrant(grant)"
                                    class="text-xs font-semibold text-red-700 underline">Entziehen</button>
                          }
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>

              <ul id="grants-cards" class="grid grid-cols-1 gap-3 md:hidden">
                @for (row of grantRows(); track row.grant.id) {
                  @let grant = row.grant;
                  <li class="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                    <div class="flex items-start justify-between gap-2">
                      <span class="font-bold tracking-tight text-gray-800">
                        {{ grant.identityName || 'Unbekanntes Konto' }}
                      </span>
                      <span class="inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs
                                   font-semibold"
                            [class]="grant.revokedAt ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'">
                        {{ grant.revokedAt ? 'Widerrufen' : 'Aktiv' }}
                      </span>
                    </div>
                    <div class="mt-1 flex flex-wrap items-center gap-2">
                      <span class="text-sm text-gray-700">{{ label(grant.role) }}</span>
                      @if (row.coverage) {
                        <span class="inline-flex shrink-0 items-center rounded-full bg-gray-100 px-2 py-0.5
                                     align-top text-xs font-semibold text-gray-600">
                          abgedeckt
                        </span>
                      }
                    </div>
                    @if (row.coverage; as coverage) {
                      <p class="text-xs text-gray-600">{{ coverage }}</p>
                    }
                    <p class="text-sm text-gray-500">{{ grant.shardId ? 'Standort' : 'Gesamte Firma' }}</p>
                    <div class="mt-3 flex flex-wrap gap-3">
                      @if (!grant.revokedAt) {
                        <button type="button" (click)="askRevokeGrant(grant)"
                                class="text-xs font-semibold text-red-700 underline">Entziehen</button>
                      }
                    </div>
                  </li>
                }
              </ul>
            }
          </section>

          <!-- ── Invitations ──────────────────────────────────────────────────────── -->
          <section class="space-y-3">
            <div>
              <h2 class="text-base font-bold tracking-tight text-gray-800">Einladungen</h2>
              <!-- Said once, above the table, so the empty code column below reads as a fact rather than a
                   defect the reader has to work out. -->
              <p class="mt-1 text-sm text-gray-500">
                Einladungscodes werden nicht gespeichert und lassen sich nachträglich nicht mehr anzeigen.
                Ist ein Code verloren, stellen Sie die Einladung neu aus.
              </p>
            </div>

            @if (listError()) {
              <p class="rounded-2xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" role="alert">
                {{ listError() }}
                <button type="button" (click)="load()" class="underline">Erneut versuchen</button>
              </p>
            }

            @if (loading()) {
              <p class="py-8 text-center text-sm text-gray-500" role="status" aria-live="polite">
                Zugänge werden geladen …
              </p>
            } @else if (!invitations().length) {
              <p class="rounded-2xl border-2 border-dashed border-gray-100 py-6 text-center text-sm italic
                        text-gray-500">
                Noch keine Einladungen.
              </p>
            } @else {
              <div id="invitations-table"
                   class="hidden overflow-x-auto rounded-2xl border border-gray-100 bg-white shadow-sm
                          md:block">
                <table class="min-w-full divide-y divide-gray-100 text-sm">
                  <thead class="bg-gray-50">
                    <tr class="text-left text-xs uppercase tracking-wide text-gray-500">
                      <th scope="col" class="px-4 py-3 font-semibold">Geltungsbereich</th>
                      <th scope="col" class="px-4 py-3 font-semibold">Rollen</th>
                      <th scope="col" class="px-4 py-3 font-semibold">Status</th>
                      <th scope="col" class="px-4 py-3 font-semibold">Gültig bis</th>
                      <th scope="col" class="px-4 py-3 text-right font-semibold">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-100">
                    @for (invitation of invitations(); track invitation.id) {
                      <tr class="transition-colors hover:bg-gray-50">
                        <td class="px-4 py-3">
                          <span class="font-medium text-gray-900">{{ scopeName(invitation.scope) }}</span>
                          @if (invitation.shardName) {
                            <span class="block text-gray-500">{{ invitation.shardName }}</span>
                          }
                          <!-- Captioned, and the note suppressed when it merely repeats the address: this
                               cell stacks several lines in one colour, so the note and the address were
                               indistinguishable even before the #279 backfill made them identical strings.
                               Since #584 the address lines are full sentences and need no caption of their
                               own, but the note still does. See note(). -->
                          @if (note(invitation); as text) {
                            <span class="block text-gray-500">
                              <span class="font-semibold">Notiz:</span> {{ text }}
                            </span>
                          }
                          <!-- Two separate facts since #584, so at most two lines and each absent when its fact
                               is. "Sent to anna@… and anybody holding the code may accept it" and "bound to
                               anna@… but never sent" are different invitations, and the old single captioned
                               address line could express neither: it printed one address and let the reader
                               guess which of the two it meant. -->
                          @if (delivered(invitation); as sentence) {
                            <span class="block text-gray-500">{{ sentence }}</span>
                          }
                          @if (binding(invitation); as sentence) {
                            <span class="block font-semibold text-gray-600">{{ sentence }}</span>
                          }
                        </td>
                        <td class="px-4 py-3 text-gray-700">{{ roleList(invitation.roles) }}</td>
                        <td class="px-4 py-3">
                          <span class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold"
                                [class]="statusClass(invitation)">{{ statusText(invitation) }}</span>
                          @if (invitation.redeemedByName) {
                            <span class="block text-gray-500">{{ invitation.redeemedByName }}</span>
                          }
                          @if (isPending(invitation)) {
                            <span class="block text-gray-500">Code nicht mehr einsehbar</span>
                          }
                        </td>
                        <td class="px-4 py-3 text-gray-500">{{ invitation.expiration | date:'dd.MM.yy HH:mm' }}</td>
                        <td class="px-4 py-3 text-right">
                          <!-- Beside „Neu ausstellen", never instead of it: the two do different things and
                               only one of them can mail anything. See the class doc. -->
                          @if (canResend(invitation)) {
                            <button type="button" (click)="askResend(invitation)" [disabled]="busy()"
                                    class="mr-3 text-xs font-semibold text-theme-primary-700 underline
                                           disabled:opacity-50">
                              Erneut senden
                            </button>
                          }
                          @if (isPending(invitation)) {
                            <button type="button" (click)="reissue(invitation)" [disabled]="busy()"
                                    class="text-xs font-semibold text-theme-primary-700 underline
                                           disabled:opacity-50">
                              Neu ausstellen
                            </button>
                          }
                          @if (!invitation.revokedAt) {
                            <button type="button" (click)="askRevokeInvitation(invitation)"
                                    class="ml-3 text-xs font-semibold text-red-700 underline">
                              Widerrufen
                            </button>
                          }
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>

              <ul id="invitations-cards" class="grid grid-cols-1 gap-3 md:hidden">
                @for (invitation of invitations(); track invitation.id) {
                  <li class="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                    <div class="flex items-start justify-between gap-2">
                      <span class="font-bold tracking-tight text-gray-800">
                        {{ invitation.shardName || scopeName(invitation.scope) }}
                      </span>
                      <span class="inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs
                                   font-semibold"
                            [class]="statusClass(invitation)">{{ statusText(invitation) }}</span>
                    </div>
                    <p class="mt-1 text-sm text-gray-700">{{ roleList(invitation.roles) }}</p>
                    <!-- Same captions and the same suppression as the table cell — a card stacks these lines
                         even more tightly, so the duplicate read worse here than anywhere. -->
                    @if (note(invitation); as text) {
                      <p class="text-sm text-gray-500"><span class="font-semibold">Notiz:</span> {{ text }}</p>
                    }
                    @if (delivered(invitation); as sentence) {
                      <p class="text-sm text-gray-500">{{ sentence }}</p>
                    }
                    @if (binding(invitation); as sentence) {
                      <p class="text-sm font-semibold text-gray-600">{{ sentence }}</p>
                    }
                    @if (invitation.redeemedByName) {
                      <p class="text-sm text-gray-500">{{ invitation.redeemedByName }}</p>
                    }
                    @if (isPending(invitation)) {
                      <p class="text-sm text-gray-500">Code nicht mehr einsehbar</p>
                    }
                    <p class="mt-1 text-sm text-gray-500">
                      Gültig bis {{ invitation.expiration | date:'dd.MM.yy HH:mm' }}
                    </p>
                    <div class="mt-3 flex flex-wrap gap-3">
                      <!-- The phone list carries the same two actions as the table row: both are rendered, and
                           only CSS decides which list is on screen. -->
                      @if (canResend(invitation)) {
                        <button type="button" (click)="askResend(invitation)" [disabled]="busy()"
                                class="text-xs font-semibold text-theme-primary-700 underline
                                       disabled:opacity-50">
                          Erneut senden
                        </button>
                      }
                      @if (isPending(invitation)) {
                        <button type="button" (click)="reissue(invitation)" [disabled]="busy()"
                                class="text-xs font-semibold text-theme-primary-700 underline
                                       disabled:opacity-50">
                          Neu ausstellen
                        </button>
                      }
                      @if (!invitation.revokedAt) {
                        <button type="button" (click)="askRevokeInvitation(invitation)"
                                class="text-xs font-semibold text-red-700 underline">Widerrufen</button>
                      }
                    </div>
                  </li>
                }
              </ul>
            }
          </section>
        }
      }
    </div>

    @if (created(); as invitation) {
      <company-invite-code-dialog [invitation]="invitation" (closed)="dismissCode()"/>
    }

    <!-- One dialog instance for both tables and both mobile card lists — see the class doc under "Revoking". -->
    @if (pendingRevoke(); as pending) {
      <company-revoke-confirm-dialog [target]="pending" [busy]="busy()"
                                      (confirmed)="confirmRevoke()" (confirmedAll)="confirmRevokeAll()"
                                      (cancelled)="cancelRevoke()"/>
    }

    <!-- Likewise one instance for the table row and the phone card: a row never carries its own prompt. -->
    @if (pendingResend(); as pending) {
      <company-resend-confirm-dialog [invitation]="pending" [busy]="busy()"
                                     (confirmed)="confirmResend()" (cancelled)="cancelResend()"/>
    }

  `
})
export class AccessManagementComponent implements OnInit {
  private readonly http = inject(GrantHttpService);
  private readonly scopes$ = inject(GrantScopeService);

  /**
   * The doorbell every other live view of "what this account may enter" listens to (#279).
   *
   * Rung after a **direct assignment** succeeds, because that one creates access for somebody who may be
   * signed in right now: a grant handed to a person looking at the context chooser has to make a tile appear
   * there, not wait for them to reload. The invite and revoke paths on this screen are unchanged — an
   * invitation opens nothing until it is redeemed, and a revoke's own refresh predates the notifier.
   */
  private readonly grantChanges = inject(GrantChangeNotifierService);

  /** The installation-wide account directory behind the assignment tab's Person picker (#749). */
  private readonly auth = inject(AuthenticationService);

  protected readonly Scope = GrantScope;

  /** The two entries of the scope picker. A field, not a template literal, because `[valueKey]` needs one. */
  protected readonly scopeOptions: ScopeOption[] = [
    {value: GrantScope.Company, label: 'Gesamte Firma'},
    {value: GrantScope.Shard, label: 'Einzelner Standort'}
  ];

  protected readonly companies = signal<GrantTargetCompany[]>([]);
  protected readonly loadingCompanies = signal(false);
  protected readonly companyId = signal<string | null>(null);
  protected readonly shards = signal<GrantTargetShard[]>([]);

  protected readonly invitations = signal<RoleGrantInvitation[]>([]);
  protected readonly grants = signal<RoleGrant[]>([]);

  /**
   * The roster, each row carrying its own coverage sentence (#759) — `null` on the overwhelming majority of
   * rows, which render exactly as before.
   *
   * Computed once per list change rather than called per row from the template: the desktop table and the
   * phone cards render the same roster twice, so a per-row call would answer the same question 2n times on
   * every change-detection pass.
   */
  protected readonly grantRows = computed(() =>
    this.grants().map(grant => ({grant, coverage: coverageSentence(grant, this.grants())})));
  protected readonly loading = signal(false);
  protected readonly busy = signal(false);
  protected readonly listError = signal<string | null>(null);
  protected readonly inviteError = signal<string | null>(null);

  /** The invitation whose code is on screen — the only moment that code exists. */
  protected readonly created = signal<RoleGrantInvitation | null>(null);

  /** Set while the revoke-confirm dialog is on screen; drives which of the four sites rendered it. */
  protected readonly pendingRevoke = signal<RevokeTarget | null>(null);

  /**
   * The invitation whose re-send is waiting for confirmation (#579), or `null` while no prompt is up.
   *
   * Held here rather than in the row, for the reason the revoke prompt is: the confirmation must not be able
   * to resize the row it was started from. It is also what makes the guarantee "nothing is sent before the
   * prompt is answered" structural — {@link confirmResend} is the only caller of the endpoint, and it reads
   * this signal.
   */
  protected readonly pendingResend = signal<RoleGrantInvitation | null>(null);

  // ----- the two tabs (#749) -----

  /** Which of the two creation paths the one form is currently asking about. */
  protected readonly tab = signal<AccessTab>('invite');

  protected readonly onInvite = computed(() => this.tab() === 'invite');
  protected readonly onDirect = computed(() => this.tab() === 'direct');

  /**
   * Whether there is more than one tab to show — i.e. whether a tab strip is rendered at all.
   *
   * A single tab is a control that cannot do anything. Rendering one anyway would also tell a company owner
   * that a second path exists which they may never take, so a session without the direct path sees exactly
   * the invitation form it saw before this feature landed.
   */
  protected readonly showTabs = computed(() => this.canGrantDirectly());

  /** The tab the panel is currently labelled by — only meaningful while a strip exists. */
  protected readonly activeTabId = computed(() => this.onDirect() ? 'tab-direct' : 'tab-invite');

  /**
   * The panel's whole class list, computed rather than assembled from `[class.x]` bindings.
   *
   * The invite surface keeps the exact utilities it always had, `bg-theme-primary-50/50` included — and a
   * slash cannot appear in a `[class.…]` binding key, so the alternative would have been to change the pink.
   * Both literals sit in this file, so Tailwind's content scan generates both.
   */
  protected readonly panelClass = computed(() => this.onDirect()
    ? 'mt-3 rounded-2xl border-[1px] border-amber-200 bg-amber-50 p-4'
    : 'mt-3 rounded-2xl border-[1px] border-theme-primary-100/50 bg-theme-primary-50/50 p-4');

  protected readonly submitLabel = computed(() =>
    this.onDirect() ? 'Rechte zuweisen' : 'Einladung erstellen');

  /**
   * The one button's whole class list.
   *
   * Computed for the same reason {@link panelClass} is: a Tailwind variant carries a colon
   * (`hover:bg-amber-700`), and a stack of six `[class.…]` bindings to express "this button is one of two
   * shapes" is harder to read than the two shapes written out. The assignment tab gets an AMBER button
   * rather than the primary one, so it does not look like the invitation beside it: this is the only write
   * in the product that creates access for somebody who is never asked. Amber and not red, because red is
   * this product's destructive colour (see the "Entziehen" actions on the roster below) and an assignment
   * destroys nothing. It matches the amber panel the button sits in.
   *
   * <b>amber-700, not the amber-600 the panel border uses.</b> The label is 14px semibold, which WCAG counts
   * as normal text and holds to 4.5:1. White on amber-600 is 3.19:1 and fails; on amber-700 it is 5.02:1.
   * Lightening this to match the panel exactly would make the button's own label the accessibility defect.
   */
  protected readonly submitClass = computed(() => {
    const base = 'inline-flex items-center justify-center rounded-2xl px-5 py-2.5 text-sm font-semibold '
      + 'text-white shadow-lg transition-all duration-200 hover:shadow-xl active:scale-95 '
      + 'disabled:cursor-not-allowed disabled:opacity-30 disabled:grayscale ';
    return base + (this.onDirect()
      ? 'bg-amber-700 shadow-amber-100 hover:bg-amber-800'
      : 'bg-theme-primary-600 shadow-theme-primary-100 hover:bg-theme-primary-700');
  });

  /** The accessible names of the two SHARED pickers follow the tab — see the template comment. */
  protected readonly scopeAriaLabel = computed(() =>
    this.onDirect() ? 'Geltungsbereich der Zuweisung' : 'Geltungsbereich der Einladung');
  protected readonly shardAriaLabel = computed(() =>
    this.onDirect() ? 'Standort der Zuweisung' : 'Standort der Einladung');

  // ----- direct assignment (#749) -----

  protected readonly identities = signal<IdentityOption[]>([]);
  protected readonly loadingIdentities = signal(false);
  protected readonly identitiesFailed = signal(false);

  protected readonly identityId = signal('');

  /**
   * Why the roles are being handed over. **Required** on this tab, unlike the invitation's optional note —
   * the server answers `E_GRANT_DIRECT_REASON_REQUIRED` for an empty one, and it is the single thing the
   * audit entry cannot reconstruct afterwards.
   */
  protected readonly reason = signal('');

  /** Whole hours, or `null` for a permanent grant — the default, so the key is omitted from the body. */
  protected readonly durationHours = signal<number | null>(null);

  protected readonly directError = signal<string | null>(null);
  protected readonly directResult = signal<DirectGrantResult | null>(null);

  /**
   * The value the duration picker carries for „Dauerhaft".
   *
   * `null` itself cannot be it: ng-select treats `null` as a defined-but-unmatched value and synthesises a
   * blank entry where the label belongs. `0` is a value the list really holds, so the control matches it, and
   * {@link setDuration} maps it back to "no key at all" — it is never sent, because the server clamps a 0 to
   * one hour and would silently produce a one-hour grant where the operator asked for a permanent one.
   */
  protected readonly PERMANENT = 0;

  /**
   * The offered time boxes. **Presets rather than a free number or a slider**, deliberately: the server
   * clamps `DurationHours`, and this form must not imply a precision it does not have. „Dauerhaft" leads
   * because it is this path's default — a direct assignment replaces an invitation, and a redeemed invitation
   * has no expiry.
   */
  protected readonly durations: {hours: number; label: string}[] = [
    {hours: this.PERMANENT, label: 'Dauerhaft'},
    {hours: 1, label: '1 Stunde'},
    {hours: 4, label: '4 Stunden'},
    {hours: 8, label: '8 Stunden'},
    {hours: 24, label: '24 Stunden'},
    {hours: 72, label: '3 Tage'}
  ];

  protected readonly durationHint = computed(() => this.durationHours() === null
    ? 'Die Rechte bleiben bestehen, bis Sie sie entziehen.'
    : 'Die Rechte laufen danach automatisch ab.');

  protected readonly scope = signal<GrantScope>(GrantScope.Company);
  protected readonly shardId = signal<string>('');
  protected readonly roles = signal<string[]>([]);
  protected readonly labelText = signal('');
  protected readonly boundEmail = signal('');

  /**
   * The issuer's explicit answer to "send it by e-mail" — `null` while they have not given one (#568).
   *
   * Stored as an override rather than as the flag itself because the flag has a *derived* default: typing a
   * valid address turns delivery ON, since handing links over by hand is what this feature exists to stop being
   * the only option. An effect writing back into a plain boolean would fight the person who just switched it
   * off — the next keystroke in the address field would turn it on again. The override survives edits to the
   * address (correcting a typo must not silently re-arm a mail the issuer declined) and is cleared only when
   * the form is reset after a successful issue.
   */
  private readonly deliverOverride = signal<boolean | null>(null);

  /**
   * The binding, as the issuer set it (#584). A plain signal, because its default is OFF and therefore needs no
   * derivation — and because the widest, least surprising invitation must be what an untouched form sends.
   */
  private readonly bindingChosen = signal(false);

  /**
   * Whether the address field currently holds something the server would accept as a binding.
   *
   * The gate on both toggles. `looksLikeEmail` is deliberately loose — see its doc: a stricter test here would
   * disable a control for an address the server would have taken, which is the failure nobody can work around
   * from this screen.
   */
  protected readonly emailUsable = computed(() => looksLikeEmail(this.boundEmail()));

  /**
   * Whether this invitation will be mailed. ON once a usable address is present, unless the issuer said
   * otherwise; always OFF without one, so the request can never carry a flag the server would refuse.
   */
  protected readonly deliverByEmail = computed(() =>
    this.emailUsable() && (this.deliverOverride() ?? true));

  /**
   * The same answer as {@link deliverByEmail}, in the vocabulary the shared segmented control speaks (#568).
   *
   * Derived rather than stored, so the control can never show a segment the request would not carry: without a
   * usable address delivery is impossible, and „Nur Link erzeugen" is then both the selection and the truth.
   */
  protected readonly deliveryChoice = computed<InviteDelivery>(() =>
    this.deliverByEmail() ? 'email' : 'link');

  /**
   * Whether this invitation may be redeemed ONLY with the address above (#584). OFF by default, and impossible
   * without an address.
   *
   * One value for both halves of the wire contract: it decides `BoundEmail` **and** `EnforceBoundEmail`
   * together, so the request can never carry the bound-but-unenforced combination that refuses nobody.
   */
  protected readonly bindToEmail = computed(() => this.emailUsable() && this.bindingChosen());

  protected readonly canManage = computed(() => this.scopes$.canManageGrants);
  protected readonly scopes = computed(() => this.scopes$.issuableScopes);
  protected readonly offeredRoles = computed(() => grantableRoles(this.scope()));
  protected readonly needsCompanyChoice = computed(() => !this.scopes$.sessionCompanyId);

  /**
   * Whether this session may hand somebody access with no invitation and no consent (#749).
   *
   * **PlatformOwner alone** — not `canManage()`, which also admits a company owner and a shard admin. This is
   * the highest-risk write in the product, so v1 keeps the blast radius at one role; the server restates the
   * same check (`E_GRANT_DIRECT_FORBIDDEN`) and is the authority, so a drift here shows up as a refusal
   * rather than as privilege.
   */
  protected readonly canGrantDirectly = computed(() => this.scopes$.isPlatformOwner);

  protected readonly companyName = computed(() => {
    const id = this.companyId();
    return this.companies().find(c => c.id === id)?.name ?? id ?? '';
  });

  /**
   * Whether the form is complete enough to send. Mirrors the server's own preconditions
   * (`RoleGrantService.Invite` rejects an empty company or an empty role set) so the common mistake is a
   * disabled button rather than a 400.
   */
  protected readonly canInvite = computed(() =>
    !this.busy()
    && !!this.companyId()
    && this.roles().length > 0
    && (this.scope() !== GrantScope.Shard || !!this.shardId()));

  /**
   * Whether the ASSIGNMENT tab is complete enough to send. The invite preconditions plus the two this path
   * adds: a target account, and a reason that is not just whitespace.
   *
   * Trimmed here as well as server-side. The server refuses a blank reason with 400
   * `E_GRANT_DIRECT_REASON_REQUIRED`, and a button that can be pressed into a guaranteed refusal is worse
   * than one that is visibly not yet armed.
   */
  protected readonly canGrantDirect = computed(() =>
    this.canInvite() && !!this.identityId() && !!this.reason().trim());

  /** Whichever of the two the visible button stands on. */
  protected readonly canSubmitCurrent = computed(() =>
    this.onDirect() ? this.canGrantDirect() : this.canInvite());

  /** The refusal belonging to the tab on screen — never the other form's leftover. */
  protected readonly currentError = computed(() =>
    this.onDirect() ? this.directError() : this.inviteError());

  protected readonly label = roleLabel;

  /** What the role actually confers — the tooltip text from `md` up, the visible hint below it. */
  protected readonly description = roleDescription;

  /**
   * Which side of `md` the window is on, and therefore which of the two description mechanisms is live. It is
   * the single source for both — see the "one description, two mechanisms" section on this class.
   */
  protected readonly compact = inject(ViewportService).isCompact;

  ngOnInit(): void {
    if (!this.canManage()) return;
    // A shard admin may only issue at shard scope, so the form starts there rather than on a scope whose
    // every submission the server would refuse.
    if (!this.scopes().includes(GrantScope.Company)) this.scope.set(GrantScope.Shard);
    this.loadCompanies();
  }

  protected loadCompanies(): void {
    this.loadingCompanies.set(true);
    this.scopes$.listCompanies().subscribe(companies => {
      this.loadingCompanies.set(false);
      this.companies.set(companies);
      // Exactly one company is not a choice — it is the session's own, or the only one there is.
      if (!this.companyId() && companies.length === 1) this.chooseCompany(companies[0].id);
    });
  }

  protected chooseCompany(id: string): void {
    this.companyId.set(id || null);
    this.shardId.set('');
    this.shards.set([]);
    this.invitations.set([]);
    this.grants.set([]);
    this.listError.set(null);
    if (!id) return;

    this.scopes$.listShards(id).subscribe(shards => {
      this.shards.set(shards);
      // One location is not a choice either, and a shard admin always has exactly one.
      if (shards.length === 1) this.shardId.set(shards[0].id);
    });
    this.load();
  }

  protected load(): void {
    const company = this.companyId();
    if (!company) return;

    this.loading.set(true);
    this.listError.set(null);

    this.http.listInvitations(company).subscribe({
      next: invitations => {
        this.invitations.set(invitations);
        this.loading.set(false);
      },
      error: err => {
        this.loading.set(false);
        this.listError.set(grantErrorMessage(err, 'Einladungen konnten nicht geladen werden.'));
      }
    });

    this.http.listGrants(company).subscribe({
      next: grants => this.grants.set(grants),
      error: err => this.listError.set(grantErrorMessage(err, 'Zugänge konnten nicht geladen werden.'))
    });
  }

  /**
   * Change the scope the invite form issues at.
   *
   * Takes the control's own output type rather than a bare {@link GrantScope}: `cqme-select` is generic over
   * multi-select too, so it is *declared* to emit `TValue | TValue[] | undefined` even where — as here — it is
   * neither multiple nor clearable. Normalising once at the entry point beats a cast in the template.
   */
  protected setScope(raw: GrantScope | GrantScope[] | undefined): void {
    const chosen = Array.isArray(raw) ? raw[0] : raw;
    const scope = chosen === GrantScope.Shard ? GrantScope.Shard : GrantScope.Company;
    this.scope.set(scope);
    // Drop roles the new scope cannot grant, rather than sending a set the server refuses wholesale — Invite
    // rejects the WHOLE request if any single role is not grantable at the scope.
    const allowed = grantableRoles(scope);
    this.roles.update(roles => roles.filter(role => allowed.includes(role)));
  }

  protected toggleRole(role: string): void {
    this.roles.update(roles => roles.includes(role) ? roles.filter(r => r !== role) : [...roles, role]);
  }

  /**
   * The issuer's explicit answer on delivery, which from now on beats the "on once there is an address"
   * default (#568).
   *
   * Takes the shared control's three-valued type even though this form offers two of them: „Keine Einladung"
   * is the roster's third segment (`allowNone` is false here), and narrowing it to a boolean at the entry
   * point is what keeps the rest of this class talking about one flag.
   */
  protected setDeliveryChoice(choice: InviteDelivery): void {
    this.deliverOverride.set(choice === 'email');
  }

  /** The issuer's answer to "only this address may redeem it" — the one #584 binding decision. */
  protected setBindToEmail(on: boolean): void {
    this.bindingChosen.set(on);
  }

  protected invite(): void {
    if (!this.canInvite()) return;
    this.busy.set(true);
    this.inviteError.set(null);

    this.http.invite(this.request()).subscribe({
      next: invitation => {
        this.busy.set(false);
        this.created.set(invitation);
        this.roles.set([]);
        this.labelText.set('');
        this.boundEmail.set('');
        // The two choices belong to the invitation that was just issued, not to the form: the next one is for
        // somebody else. Both fall back to their declared defaults (delivery on once an address is typed, the
        // binding off), so the next invitation is not quietly issued under the previous one's rules.
        this.deliverOverride.set(null);
        this.bindingChosen.set(false);
        this.load();
      },
      error: err => {
        this.busy.set(false);
        // A refusal here is ordinary: GrantIssuancePolicy scopes more narrowly than the controller's coarse
        // [Authorize] list, so it is rendered as the server's own sentence rather than treated as a fault.
        this.inviteError.set(grantErrorMessage(err, 'Die Einladung konnte nicht erstellt werden.'));
      }
    });
  }

  /**
   * Re-issue an invitation whose code is gone: create an identical one, then revoke the original.
   *
   * **That order is deliberate.** Revoking first would, on a failed re-issue, leave the company with neither
   * the old invitation nor a new one — and revoking an invitation also revokes the grants it produced. Issuing
   * first means the worst case is one redundant pending invitation, which is visible and revocable.
   */
  protected reissue(invitation: RoleGrantInvitation): void {
    if (this.busy()) return;
    this.busy.set(true);
    this.inviteError.set(null);

    this.http.invite({
      Scope: invitation.scope,
      CompanyId: invitation.companyId,
      ShardId: invitation.scope === GrantScope.Shard ? (invitation.shardId ?? null) : null,
      Roles: [...invitation.roles],
      Label: invitation.label ?? null,
      BoundEmail: invitation.boundEmail ?? null,
      // Enforcement is a stored property of the invitation, so an identical re-issue has to carry it — dropping
      // it would silently hand back a WEAKER invitation under the same row, which is the one direction nobody
      // would notice. Delivery is not stored (it describes the act of issuing, not the invitation) and is
      // deliberately NOT re-attempted: re-issuing is the action taken when the code was lost, so the person
      // doing it is standing in front of the dialog waiting for a link to hand over.
      EnforceBoundEmail: invitation.enforceBoundEmail ?? false
    }).subscribe({
      next: fresh => {
        this.created.set(fresh);
        this.http.revokeInvitation(invitation.id).subscribe({
          next: () => {
            this.busy.set(false);
            this.load();
          },
          error: err => {
            this.busy.set(false);
            // Reload FIRST: `load()` clears `listError` as its opening move, so setting the message before it
            // would wipe the one line explaining that the old invitation is still live.
            this.load();
            this.listError.set(grantErrorMessage(err,
              'Die neue Einladung wurde erstellt, die alte konnte aber nicht widerrufen werden.'));
          }
        });
      },
      error: err => {
        this.busy.set(false);
        this.inviteError.set(grantErrorMessage(err, 'Die Einladung konnte nicht neu ausgestellt werden.'));
      }
    });
  }

  // ----- re-sending (#579) -----

  /**
   * Whether this invitation may be offered the „Erneut senden" button.
   *
   * The shared predicate, not a second one written here: the employee screen asks the same question, and two
   * copies of it would drift into a button offered into a guaranteed refusal. See
   * {@link invitationCanBeResent} for why expiry is not disqualifying.
   */
  protected canResend(invitation: RoleGrantInvitation): boolean {
    return invitationCanBeResent(invitation);
  }

  /** Opens the confirmation. Nothing is sent until {@link confirmResend} runs. */
  protected askResend(invitation: RoleGrantInvitation): void {
    if (this.busy()) return;
    this.pendingResend.set(invitation);
  }

  /** Cancel button, backdrop click or Escape — the invitation is untouched, and its code still works. */
  protected cancelResend(): void {
    this.pendingResend.set(null);
  }

  /**
   * Re-send the confirmed invitation.
   *
   * The response carries the **replacement's** plaintext code and the delivery outcome, so it goes into the
   * same {@link created} signal an issue does and is shown by the same dialog. That is not a convenience: the
   * server returns the code regardless of what happened to the mail, and a screen that reported only "sent"
   * would turn a bounced or filtered message into an invitation nobody can reach.
   *
   * One call at a time, on the shared {@link busy} flag rather than a private one: a second re-send would
   * withdraw the replacement the first one just created, and the code on screen would silently stop working.
   */
  protected confirmResend(): void {
    const invitation = this.pendingResend();
    if (!invitation || this.busy()) return;

    this.busy.set(true);
    this.http.resendInvitation(invitation.id).subscribe({
      next: fresh => {
        this.busy.set(false);
        this.pendingResend.set(null);
        this.created.set(fresh);
        this.load();
      },
      error: err => {
        this.busy.set(false);
        this.pendingResend.set(null);
        // Reload FIRST: `load()` clears `listError` as its opening move, so setting the message before it
        // would wipe the very line explaining the refusal — the same ordering trap `reissue` documents.
        this.load();
        this.listError.set(resendErrorMessage(err, 'Die Einladung konnte nicht erneut gesendet werden.'));
      }
    });
  }

  protected askRevokeInvitation(invitation: RoleGrantInvitation): void {
    this.pendingRevoke.set({kind: 'invitation', invitation});
  }

  /**
   * The survivor set is computed **here, at open time**, from the roster the operator is looking at — not
   * inside the dialog, which is deliberately given only what it must render. That also means the list the
   * dialog names is the same data the `[abgedeckt]` badges beside it were drawn from, so the two can never
   * disagree on screen.
   */
  protected askRevokeGrant(grant: RoleGrant): void {
    this.pendingRevoke.set({
      kind: 'grant', grant,
      superseded: supersededGrants(grant, this.grants()).map(other => this.withShardName(other))
    });
  }

  /**
   * A grant with its location's NAME filled in from the loaded shard list.
   *
   * `RoleGrantDto` carries `shardId` but no `shardName`, so `grantScopeName`'s fallback printed a raw GUID
   * into the dialog's survivor list — "Standortverwaltung · 12cb656e-4c5f-…", which names nothing an operator
   * can act on. The roster's own rows do not hit this because they render the location from a different path;
   * only the dialog reads the grant directly.
   *
   * Resolved from `shards()` rather than by widening the DTO: the list is already loaded for the shard picker
   * and is the same source the rest of this screen names locations from, so the two cannot disagree. A shard
   * that is genuinely missing from it still falls back to the id, which is the honest answer when the name is
   * unknown.
   */
  private withShardName(grant: RoleGrant): RoleGrant {
    if (!grant.shardId || grant.shardName) return grant;
    const known = this.shards().find(shard => shard.id === grant.shardId);
    return known ? {...grant, shardName: known.name} : grant;
  }

  /** Cancel button, backdrop click or Escape inside the dialog — all arrive here, all leave everything alone. */
  protected cancelRevoke(): void {
    this.pendingRevoke.set(null);
  }

  /** The dialog's own "confirmed" output — routes to the right endpoint by what is actually pending. */
  protected confirmRevoke(): void {
    const pending = this.pendingRevoke();
    if (!pending) return;
    if (pending.kind === 'invitation') this.confirmRevokeInvitation(pending.invitation);
    else this.confirmRevokeGrant(pending.grant);
  }

  private confirmRevokeInvitation(invitation: RoleGrantInvitation): void {
    if (this.busy()) return;
    this.busy.set(true);
    this.http.revokeInvitation(invitation.id).subscribe({
      next: () => {
        this.busy.set(false);
        this.pendingRevoke.set(null);
        this.load();
      },
      error: err => {
        this.busy.set(false);
        this.pendingRevoke.set(null);
        this.listError.set(grantErrorMessage(err, 'Die Einladung konnte nicht widerrufen werden.'));
      }
    });
  }

  /**
   * „Alle N entziehen" (#759): the grant **and** every still-active grant it covers, in one call.
   *
   * One request rather than a loop over {@link GrantHttpService.revokeGrant}, so there is a single verdict —
   * "three of five roles were withdrawn, and the screen says it failed" is the worst outcome a privilege
   * change can have. Guarded on the survivor list being non-empty, so this can never widen a revoke the
   * dialog did not show a list for.
   */
  protected confirmRevokeAll(): void {
    const pending = this.pendingRevoke();
    if (!pending || pending.kind !== 'grant' || this.busy()) return;
    const superseded = pending.superseded ?? [];
    if (!superseded.length) return;

    this.busy.set(true);
    this.http.revokeGrants([pending.grant.id, ...superseded.map(other => other.id)]).subscribe({
      next: () => {
        this.busy.set(false);
        this.pendingRevoke.set(null);
        this.load();
      },
      error: err => {
        this.busy.set(false);
        this.pendingRevoke.set(null);
        this.listError.set(grantErrorMessage(err, 'Die Rollen konnten nicht entzogen werden.'));
      }
    });
  }

  private confirmRevokeGrant(grant: RoleGrant): void {
    if (this.busy()) return;
    this.busy.set(true);
    this.http.revokeGrant(grant.id).subscribe({
      next: () => {
        this.busy.set(false);
        this.pendingRevoke.set(null);
        this.load();
      },
      error: err => {
        this.busy.set(false);
        this.pendingRevoke.set(null);
        this.listError.set(grantErrorMessage(err, 'Die Rolle konnte nicht entzogen werden.'));
      }
    });
  }

  protected dismissCode(): void {
    this.created.set(null);
  }

  // ----- the two tabs (#749) -----

  /**
   * Switch tabs.
   *
   * The SHARED answers — scope, location, roles — are deliberately left alone: somebody who filled them in on
   * one tab and realised they wanted the other path should not have to type them again, and that is the whole
   * argument for one form over two. What is cleared is the state that belongs to the tab being LEFT: its
   * refusal, and the confirmation of a grant that has already happened.
   *
   * Guarded on the role, so the tab cannot be reached by a stray call even if the strip is somehow rendered.
   */
  protected selectTab(tab: AccessTab): void {
    if (tab === 'direct' && !this.canGrantDirectly()) return;
    if (this.tab() === tab) return;

    this.tab.set(tab);
    this.inviteError.set(null);
    this.directError.set(null);
    this.directResult.set(null);

    // Fetched on demand rather than on every render of this screen: the account directory is an
    // installation-wide, PlatformOwner-gated read, and most visits here never open this tab.
    if (tab === 'direct' && !this.identities().length && !this.loadingIdentities()) this.loadIdentities();
  }

  /**
   * Arrow-key navigation across the tab strip (WAI-ARIA APG).
   *
   * Automatic activation — moving focus selects — which is what APG recommends when switching costs nothing,
   * and switching here costs nothing: the shared answers survive and no request is sent. `Home`/`End` jump to
   * the ends, as the pattern requires; everything else is left to the browser.
   */
  protected onTabKeydown(event: KeyboardEvent): void {
    const order: AccessTab[] = ['invite', 'direct'];
    const current = order.indexOf(this.tab());
    let next: number;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (current + 1) % order.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (current - 1 + order.length) % order.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = order.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.selectTab(order[next]);
    // Focus follows the selection, or the strip's single tab stop would be left on a tab nobody is on.
    (document.getElementById(this.activeTabId()) as HTMLElement | null)?.focus();
  }

  /** The one button on the form, routed by the tab it is standing on. */
  protected submitCurrent(): void {
    if (this.onDirect()) this.grantDirectly();
    else this.invite();
  }

  // ----- direct assignment (#749) -----

  /** (Re)load the installation-wide account directory the Person picker offers. */
  protected loadIdentities(): void {
    this.loadingIdentities.set(true);
    this.identitiesFailed.set(false);
    this.auth.listAllIdentities().subscribe({
      next: list => {
        this.loadingIdentities.set(false);
        this.identities.set(list.map(identity => ({id: identity.id, label: describeIdentity(identity)})));
      },
      error: () => {
        this.loadingIdentities.set(false);
        this.identitiesFailed.set(true);
        this.identities.set([]);
      }
    });
  }

  protected setDuration(raw: number | number[] | undefined): void {
    const chosen = Array.isArray(raw) ? raw[0] : raw;
    if (chosen === undefined) return;
    this.durationHours.set(chosen === this.PERMANENT ? null : chosen);
  }

  /**
   * Hand the chosen account its roles outright.
   *
   * On success the lists behind the form reload AND {@link GrantChangeNotifierService} is rung: unlike an
   * invitation, this grant is live the moment it exists, so a session signed in right now must see its new
   * tile without reloading the app.
   */
  private grantDirectly(): void {
    if (!this.canGrantDirect()) return;
    this.busy.set(true);
    this.directError.set(null);
    this.directResult.set(null);

    const hours = this.durationHours();
    this.http.grantDirect({
      IdentityId: this.identityId(),
      Scope: this.scope(),
      CompanyId: this.companyId()!,
      ShardId: this.scope() === GrantScope.Shard ? this.shardId() : null,
      Roles: this.roles(),
      Reason: this.reason().trim(),
      // Spread, so a permanent grant sends NO key rather than `undefined` or a 0 — the server clamps a 0 to
      // one hour, which would quietly produce a time-boxed grant where a permanent one was asked for.
      ...(hours === null ? {} : {DurationHours: hours})
    }).subscribe({
      next: result => {
        this.busy.set(false);
        this.directResult.set(result);
        // The answers that belonged to THIS assignment, cleared for the next one. Scope, location and roles
        // stay: handing two people the same access is the common case, and the confirmation above names what
        // was just done, so nothing is ambiguous.
        this.identityId.set('');
        this.reason.set('');
        this.load();
        this.grantChanges.notifyChanged();
      },
      error: err => {
        this.busy.set(false);
        // German, mapped from the CODE. `description` is the server's developer-facing English and must never
        // be rendered (style guide, "Surfacing an API error"). The fallback is a real code path here: an
        // unknown company or shard answers a bare 404 carrying no ApiError at all.
        this.directError.set(directGrantErrorMessage(err,
          'Die Rechte konnten nicht zugewiesen werden. Bitte versuchen Sie es erneut.'));
      }
    });
  }

  /**
   * The confirmation sentence, built from what the SERVER said it created rather than from the form —
   * including the expiry it stamped, so the operator is told exactly how long the access lasts rather than
   * having to remember the picker's last position.
   */
  protected directConfirmation(result: DirectGrantResult): string {
    // The SERVER's name first: it resolved the identity row at write time, so it is the one answer that
    // cannot be a stale copy of a directory loaded minutes ago. The picker's own label is the fallback.
    const person = result.identityName
      || this.identities().find(i => i.id === result.identityId)?.label
      || result.identityId;
    const roles = roleListLabel(result.granted.map(g => g.role));
    const where = directGrantTargetLabel({...result, companyName: result.companyName || this.companyName()});
    // Falsy, not `!== null`: an omitted key is `undefined`, and `=== null` would be false for it — printing
    // "laufen am undefined Uhr ab" on the path this form takes by DEFAULT.
    const expiry = result.expiresAt
      ? ` Die Rechte laufen am ${formatExpiry(result.expiresAt)} automatisch ab.`
      : ' Die Rechte bleiben bestehen, bis Sie sie entziehen.';
    return `${person} hält jetzt ${roles} für ${where}.${expiry}`;
  }

  // ----- display helpers -----

  protected value(event: Event): string {
    return (event.target as HTMLInputElement).value;
  }

  /** The new state of a switch, from its own `change` event. The `value()` sibling for a checkbox input. */
  protected checked(event: Event): boolean {
    return (event.target as HTMLInputElement).checked;
  }

  /**
   * Narrows what a single-value `cqme-select` emits down to the one id these pickers store.
   *
   * The control's output type is `TValue | TValue[] | undefined` because the same component also does
   * multi-select; neither the company nor the shard picker is `[multiple]`, so an array can never actually
   * arrive. Clearing *can*, and it arrives as `undefined` — mapped to `''`, which is what both call sites
   * already read as "nothing chosen".
   */
  protected one(value: string | string[] | undefined): string {
    return (Array.isArray(value) ? value[0] : value) ?? '';
  }

  /**
   * The issuer's own note — or `null` when it says nothing the bound-email line below it does not already.
   *
   * `BackfillPayrollInvitationsAsRoleGrants` seeds `Label` from the payroll account's address, so every
   * backfilled invitation carries `Label == BoundEmail` and would otherwise print the same address on two
   * consecutive lines, reading as a duplicated render rather than as data. Compared trimmed and
   * case-insensitively rather than with `===`: a note that differs from the address only in case or padding is
   * just as redundant, and the point is to drop a line that carries no information — not to special-case one
   * backfill's exact output.
   */
  protected note(invitation: RoleGrantInvitation): string | null {
    const label = invitation.label?.trim();
    if (!label) return null;
    const email = invitation.boundEmail?.trim();
    return email && label.toLowerCase() === email.toLowerCase() ? null : label;
  }

  /**
   * The binding line for a list row, or `null` when there is none — which is the ordinary case since #584.
   *
   * The wording comes from `grant.models.ts` rather than from this template, so the sentence the issuer switched
   * on, the one the result dialog prints back and the one this list shows are literally the same string.
   */
  protected readonly binding = bindingSentence;

  /** The delivery line for a list row, or `null` for an invitation nobody asked to mail. */
  protected readonly delivered = deliverySentence;

  protected scopeName(scope: GrantScope): string {
    return scopeLabel(scope);
  }

  protected readonly roleList = roleListLabel;

  protected status(invitation: RoleGrantInvitation): InvitationStatus {
    return invitationStatus(invitation);
  }

  protected statusText(invitation: RoleGrantInvitation): string {
    return invitationStatusLabel(this.status(invitation));
  }

  protected statusClass(invitation: RoleGrantInvitation): string {
    return invitationStatusClass(this.status(invitation));
  }

  /** Pending is the one state where the missing code is worth naming — the others never had one to show. */
  protected isPending(invitation: RoleGrantInvitation): boolean {
    return this.status(invitation) === 'pending';
  }

  /**
   * The invite request, with the typed address routed to whichever field the binding toggle selected (#584).
   *
   * The address lands in **exactly one** of `BoundEmail` and `DeliveryEmail`, never both: the server answers
   * `E_GRANT_INVITE_DELIVERY_ADDRESS_CONFLICT` for two addresses that disagree, and sending the same string
   * twice would leave the next reader of this method wondering which of the two the toggle actually decides.
   *
   * Every flag reads off the computed signals, which already fold in "there is a usable address" — so a request
   * can never carry one the server would refuse with `E_GRANT_INVITE_EMAIL_REQUIRED`, and the mapping for that
   * code exists for the case this screen cannot predict (an address it accepts and the server does not).
   */
  private request(): RoleGrantInviteRequest {
    const shard = this.scope() === GrantScope.Shard ? this.shardId() : '';
    const address = this.boundEmail().trim() || null;
    const bind = this.bindToEmail();
    const deliver = this.deliverByEmail();

    return {
      Scope: this.scope(),
      CompanyId: this.companyId() ?? '',
      ShardId: shard || null,
      Roles: [...this.roles()],
      Label: this.labelText().trim() || null,
      BoundEmail: bind ? address : null,
      DeliverByEmail: deliver,
      EnforceBoundEmail: bind,
      // Only when the invitation is NOT bound: a bound one is already mailed to its binding, and naming the
      // same address twice is what the conflict code exists to refuse.
      DeliveryEmail: !bind && deliver ? address : null
      // Origin is deliberately omitted: the controller prefers the request's own Origin header, which the
      // browser sends for us. See RoleGrantInviteRequest.Origin.
    };
  }
}

/**
 * How one account reads in the assignment tab's Person picker: „Name (adresse@example.com) · Microsoft".
 *
 * All three parts, joined into the ONE string `cqme-select` searches — a display name alone is not unique on
 * an installation-wide directory (two people called Meier), and an address alone is not recognisable. The
 * provider comes last because it is the tie-breaker, not the identifier.
 */
function describeIdentity(identity: ExternalIdentityInfo): string {
  const name = identity.displayName?.trim();
  const email = identity.email?.trim();
  const who = name && email ? `${name} (${email})` : (name || email || identity.id);
  return identity.provider ? `${who} · ${identity.provider}` : who;
}

/** "TT.MM.JJJJ, HH:MM Uhr" — the date is included because even a short time box can run past midnight. */
function formatExpiry(iso: string): string {
  const formatted = new Date(iso).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
  return `${formatted} Uhr`;
}
